import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Lightbox from 'yet-another-react-lightbox'
import Video from 'yet-another-react-lightbox/plugins/video'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import { KTSVG } from '../../../_metronic/helpers'
import UserProfileHeader from './components/UserProfileHeader'
import UserProfileFooter from './components/UserProfileFooter'
import { ReportsMediaFilters } from './components/ReportsMediaFilters'
import {
  getAllReportsAction,
  // aggregateFilter,
  // reportsMediaFilterSelector,
  reportsMediaStatusSelector,
  reportsMediaPaginationSelector,
  reportsSelector,
  changeMediaPrivacy,
} from '../../reducers/reportsReducer'
import heic2any from 'heic2any'
import MUITable from '../../modules/common/MUITable'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import TableLoading from '../../../_metronic/layout/components/Common/TableSkeletonLoading'


type Props = {}

const Report: FC<Props> = () => {

  const dispatch: any = useDispatch()
  const [open, setOpen] = useState(false)
  const [itemIndex, setItemIndex] = useState<number>(0)
  const [convertedImages, setConvertedImages] = useState<{ [key: string]: string }>({})
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)

  const reports = useSelector(reportsSelector)
  const pagination = useSelector(reportsMediaPaginationSelector)
  // const filtersData = useSelector(reportsMediaFilterSelector)
  const status = useSelector(reportsMediaStatusSelector);

  useEffect(() => {
    dispatch(
      getAllReportsAction({
        page: currentPage,
        perPage: rowsPerPage,
        sort: 'id|desc',
      })
    )
  }, [currentPage, rowsPerPage])

  // const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const query = e.target.value
  //   dispatch(aggregateFilter({ key: 'query', value: query.length > 2 ? query.toUpperCase() : '' }))
  // }

  const [loadingPercentage, setLoadingPercentage] = useState<{ [id: string]: number }>({});

  const changeMediaPrivacyFunction = async (params: { mediaId: number; privacy: number }) => {
    try {
      await dispatch(changeMediaPrivacy(params));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const convertHeicToJpeg = async (thumbnail: string, id: string) => {
      try {
        if (convertedImages[id]) return;
        if (thumbnail.toLowerCase().endsWith('.heic')) {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', thumbnail, true);
          xhr.responseType = 'blob';

          xhr.onprogress = (event) => {
            if (event.lengthComputable) {
              // Download progress: 0-80%
              const percentComplete = (event.loaded / event.total) * 80;
              setLoadingPercentage((prev) => {
                const updated = {
                  ...prev,
                  [id]: Math.round(percentComplete),
                };
                return updated;
              });
            }
          };

          xhr.onload = async () => {
            if (xhr.status === 200) {
              const blob = xhr.response;
              // Download complete, set to 80%
              setLoadingPercentage((prev) => ({ ...prev, [id]: 80 }));

              try {
                // Simulate conversion progress from 80% to 99%
                let conversionProgress = 80;
                const interval = setInterval(() => {
                  conversionProgress += 4;
                  setLoadingPercentage((prev) => ({ ...prev, [id]: Math.min(conversionProgress, 99) }));
                  if (conversionProgress >= 99) clearInterval(interval);
                }, 100);

                const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.8 });
                clearInterval(interval);
                setLoadingPercentage((prev) => ({ ...prev, [id]: 100 }));

                const jpegUrl = URL.createObjectURL(converted as Blob);
                setConvertedImages((prev) => ({ ...prev, [id]: jpegUrl }));
              } catch (err) {
                console.error("HEIC conversion failed:", err);
              }

              // Clean up percentage state after a short delay to allow image to render
              setTimeout(() => {
                setLoadingPercentage((prev) => {
                  const { [id]: _, ...rest } = prev;
                  return rest;
                });
              }, 500);
            }
          };

          xhr.onerror = () => {
            console.error('Error fetching HEIC image');
            setLoadingPercentage((prev) => ({
              ...prev,
              [id]: 0,
            }));
          };

          xhr.send();
        } else {
          setConvertedImages((prev) => ({ ...prev, [id]: thumbnail }));
        }
      } catch (err) {
        console.error('HEIC conversion failed:', err);
      }
    };

    reports.forEach((report: any) => {
      const thumbnail = report?.media?.thumbnail
      const id = report?.media?.id || thumbnail
      if (thumbnail?.toLowerCase().endsWith('.heic') && !convertedImages[id]) {
        convertHeicToJpeg(thumbnail, id)
      }
    })
  }, [reports, convertedImages])

  const uploadedByName = (row: any) => {
    return (
      row?.media?.uploadedBy?.firstName || row?.media?.uploadedBy?.lastName
        ? (row.media.uploadedBy.firstName ? row.media.uploadedBy.firstName : '') +
        (row.media.uploadedBy.lastName ? ' ' + row.media.uploadedBy.lastName : '')
        : 'N/A'
    )
  }

  const reportedByName = (row: any) => {
    return (
      row?.reportedBy?.firstName || row?.reportedBy?.lastName
        ? (row.reportedBy.firstName ? row.reportedBy.firstName : '') +
        (row.reportedBy.lastName ? ' ' + row.reportedBy.lastName : '')
        : 'N/A'
    )
  }

  const columns = [
    {
      id: 'index',
      label: '#',
      align: 'center',
      render: (_: any, i: number) => i + 1 + (currentPage - 1) * rowsPerPage,
    },
    {
      id: 'thumbnail',
      label: 'Thumbnail',
      align: 'center',
      render: (row: any, i: number) => {
        const isHeic = row?.media?.thumbnail?.toLowerCase().endsWith('.heic');
        const id = row?.media?.id || row?.media?.thumbnail;
        const src = isHeic ? convertedImages[id] : row?.media?.thumbnail;

        const percentage = loadingPercentage[id];
        const isConverted = !!convertedImages[id];

        if (isHeic && !isConverted) {
          return (
            <div
              style={{
                width: 70,
                height: 70,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                backgroundColor: '#f0f0f0',
              }}
              className='text-primary'
            >
              {typeof percentage === 'number' && percentage < 100 ? (
                <span>{percentage}%</span>
              ) : null}
            </div>
          );
        }

        return (
          <img
            src={src}
            alt='media'
            style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
            onClick={() => {
              setItemIndex(i);
              setOpen(true);
            }}
          />
        );
      },
    },

    {
      id: 'type',
      label: 'Type',
      align: 'center',
      render: (row: any) => row?.media?.type || 'N/A',
    },
    {
      id: 'caption',
      label: 'Caption',
      align: 'center',
      render: (row: any) =>
        <div style={{ maxWidth: '200px', whiteSpace: 'normal' }}>
          {row?.media.caption || 'N/A'}
        </div>
    },
    {
      id: 'reportReason',
      label: 'Report Reason',
      align: 'center',
      render: (row: any) => (
        <div style={{ maxWidth: '200px', whiteSpace: 'normal' }}>
          {row?.reason || 'N/A'}
        </div>
      ),
    },
    {
      id: 'reportedBy',
      label: 'Reported By',
      align: 'center',
      render: (row: any) => {
        return reportedByName(row);
      }
    },
    {
      id: 'uploadedBy',
      label: 'Uploaded By',
      align: 'center',
      render: (row: any) => {
        return uploadedByName(row);
      }
    },
    {
      id: 'reportedAt',
      label: 'Reported At',
      align: 'center',
      render: (row: any) => {
        const date = new Date(row?.reportedAt);
        return date.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
      },
    },
    {
      id: 'createdAt',
      label: 'Created At',
      align: 'center',
      render: (row: any) => {
        const date = new Date(row?.media.createdAt);
        return date.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
      },
    },
    {
      id: 'privacy',
      label: 'Change Privacy',
      align: 'center',
      render: (row: any, i: number) => (
        <div
          className='d-flex align-items-center justify-content-start pointer'
          onClick={() =>
            changeMediaPrivacyFunction({
              mediaId: row?.mediaId,
              privacy: row?.media?.isAdminPrivate === 1 ? 0 : 1,
            })
          }
        >
          {row?.media?.isAdminPrivate === 1 ? <FaEye /> : <FaEyeSlash />}
          <p style={{ fontSize: '12px' }} className='my-0 ms-1'>
            {row?.media?.isAdminPrivate === 1
              ? 'Reviewed - Make Public'
              : row?.media?.isAdminPrivate === 0
                ? 'Make Private'
                : ''}
          </p>
        </div>
      ),
    },
    {
      id: 'actions',
      label: 'View',
      align: 'center',
      render: (_: any, i: number) => (
        <button
          className='btn btn-sm btn-light-primary d-flex justify-content-start'
          onClick={() => {
            setItemIndex(i)
            setOpen(true)
          }}
        >
          <FaEye color='black' />
        </button>
      ),
    },
  ]

  const structuredRows = reports?.map((row: any, index: number) => {
    const newRow: any = {}
    columns.forEach((col) => {
      newRow[col.id] = col.render ? col.render(row, index) : row[col.id]
    })
    newRow.id = row.id
    return newRow
  })

  const tableData = {
    table: {
      columns,
      rows: structuredRows,
    },
    pagination: {
      total: pagination?.total || 0,
      lastPage: pagination?.lastPage || 1,
    },
  }

  return (
    <>
      <div className='row mt-5 mb-5 mt-n5 pt-n5'>
        <div className='col-6'>
          <h2 className='py-3 mb-2 pt-5 mt-5'>Reports</h2>
        </div>
        {/* <div className='col-6 align-self-center text-end'>
          <ReportsMediaFilters filtersData={filtersData} />
        </div> */}
      </div>

      <div className='card mb-5 mb-xl-10 pt-4'>
        {/* <div className='d-flex align-items-center justify-content-end my-3 px-4'>
          <KTSVG path='/media/icons/duotune/general/gen021.svg' className='svg-icon-1 position-absolute me-5' />
          <input
            onChange={handleSearchChange}
            type='text'
            className='form-control form-control-solid w-250px ps-14'
            placeholder='Search media'
          />
        </div> */}

        <div
          style={{
            overflow: 'auto',
            whiteSpace: 'nowrap',
          }}
          className='card mb-5 mb-xl-10 p-5'
        >
          {status === 'loading' ? (
            <TableLoading />
          ) : (
            <MUITable
              data={tableData}
              page={currentPage}
              perPage={rowsPerPage}
              setPage={setCurrentPage}
              setPerPage={setRowsPerPage}
            />
          )}
        </div>

        <Lightbox
          open={open}
          index={itemIndex}
          close={() => setOpen(false)}
          slides={reports.map((data: any) => {
            const url = data?.media?.url
            const isHeic = url?.toLowerCase().endsWith('.heic')
            const id = data?.media?.id
            const src = isHeic && convertedImages[id] ? convertedImages[id] : url

            if (data?.media?.type.includes('image')) {
              return {
                src,
                title: <UserProfileHeader data={data} uploadedByName={uploadedByName(data)} />,
                description: <UserProfileFooter item={data} reportedByName={reportedByName(data)} />,
              }
            } else {
              return {
                type: 'video',
                width: 1050,
                height: 550,
                poster: url,
                title: <UserProfileHeader data={data} uploadedByName={uploadedByName(data)} />,
                description: <UserProfileFooter item={data} reportedByName={reportedByName(data)} />,
                sources: [{ src: url, type: `${data?.media?.type}/mp4` }],
              }
            }
          })}
          video={{ autoPlay: true, controls: true }}
          plugins={[Captions, Video]}
          styles={{
            container: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
        />
      </div>
    </>
  )
}

export { Report }